// frontend/src/pages/support/SupportArticle.js
//
// Renders the /p/<slug> support articles inherited from the pre-rebuild site.
// See data/supportArticles.js for why these URLs matter.
//
// An unknown /p/ slug renders the 404 rather than redirecting: these URLs are
// indexed, and a silent redirect to the hub would hide the fact that a ranking
// page is missing content.
import '../../custom.css'

import React from 'react'
import { Container, Button, Image } from 'react-bootstrap'
import { useParams, Link } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL, BRAND } from '../../components/Seo'
import { getSupportArticle } from '../../data/supportArticles'
import NotFound from '../NotFound'
import ProductBlock from '../../components/ProductBlock'

export default function SupportArticle () {
  const { slug } = useParams()
  const article = getSupportArticle(slug)

  if (!article) return <NotFound />

  const url = `${SITE_URL}/p/${article.slug}`

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: article.updated || article.date,
    author: { '@type': 'Organization', name: BRAND },
    publisher: {
      '@type': 'Organization',
      name: BRAND,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/assets/logo/greatly-social-full.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  }

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Support', item: `${SITE_URL}/p/support` },
      { '@type': 'ListItem', position: 2, name: article.title, item: url },
    ],
  }

  return (
    <>
      <Seo
        title={article.title}
        description={article.excerpt}
        path={`/p/${article.slug}`}
        type='article'
        publishedAt={article.date}
        modifiedAt={article.updated || article.date}
        jsonLd={[faqLd, breadcrumbLd]}
      />

      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='small text-muted mb-2'>
            <Link to='/p/support' className='text-decoration-none'>&larr; Support</Link>
            {article.platform && <> &middot; {article.platform}</>}
          </p>

          <h1 className='fw-bold mb-4'>{article.title}</h1>

          {article.image && (
            <Image
              src={article.image}
              alt={article.imageAlt || ''}
              fluid
              className='rounded-3 mb-4 w-100'
              style={{ maxHeight: 300, objectFit: 'cover' }}
            />
          )}

          {/* eslint-disable-next-line react/no-danger */}
          <div className='support-body' dangerouslySetInnerHTML={{ __html: article.html }} />

          {article.inlineImage && (
            <figure className='my-4'>
              <Image src={article.inlineImage} alt={article.inlineImageAlt || ''} fluid
                     className='rounded-3 border w-100' />
              {article.inlineImageAlt && (
                <figcaption className='text-muted small mt-2'>{article.inlineImageAlt}</figcaption>
              )}
            </figure>
          )}

          {article.showProduct && <ProductBlock lead={article.productLead} />}

          <hr className='my-5' />

          {/* Article-specific CTA. The benefit named is the one that solves THIS
              article's problem — a generic "try it free" converts far worse on a
              page about expired connections than "we email you before one expires". */}
          {article.cta && (
            <div className='p-4 p-md-5 rounded-3 mb-4'
                 style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
              <h2 className='h4 fw-bold mb-2'>{article.cta.title}</h2>
              <p className='mb-3' style={{ color: 'rgba(255,255,255,.85)' }}>{article.cta.body}</p>
              <ul className='list-unstyled mb-4'>
                {article.cta.bullets.map(b => (
                  <li key={b} className='d-flex align-items-start gap-2 mb-2'
                      style={{ color: 'rgba(255,255,255,.92)' }}>
                    <CheckCircleFill className='mt-1 flex-shrink-0' />
                    <span className='small'>{b}</span>
                  </li>
                ))}
              </ul>
              <Button variant='light' href={article.cta.primary[1]} className='me-2 mb-2 fw-semibold'>
                {article.cta.primary[0]}
              </Button>
              <Button variant='outline-light' href={article.cta.secondary[1]} className='mb-2'>
                {article.cta.secondary[0]}
              </Button>
            </div>
          )}

          <div className='p-4 rounded-3' style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
            <h2 className='h6 fw-bold mb-2'>Still stuck?</h2>
            <p className='text-muted small mb-3'>
              If reconnecting the account did not help, tell us which account and when the post
              failed and we will look into it.
            </p>
            <Button variant='primary' size='sm' href='/contact' className='me-2'>Contact support</Button>
            <Button variant='outline-secondary' size='sm' href='/p/support'>All support articles</Button>
          </div>
        </Container>
      </section>
    </>
  )
}
